import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

const CATEGORIES = ['tractor', 'skid_steer', 'excavator', 'mower', 'sxs', 'pump', 'pressure_washer', 'wood_splitter', 'chainsaw', 'fence', 'coop', 'tank', 'solar', 'implement', 'attachment', 'other'];

// Add a machine — or, at /equipment/:id/edit, change an existing one.
export default function EquipmentNew() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const editing = !!editId;
  const { isOwner } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [form, setForm] = useState({
    make: '', model: '', year: '', serial: '', category: 'tractor',
    hour_meter: '', purchase_date: '', purchase_price_cents: '',
    primary_maintainer_id: '', backup_maintainer_id: '', parent_id: '', notes: ''
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/users'),
      api.get('/api/equipment'),
      editing ? api.get(`/api/equipment/${editId}`) : api.get('/api/equipment-defaults'),
    ]).then(([{ users }, { equipment }, third]) => {
      setUsers(users);
      // A machine can't be its own parent
      setEquipment(equipment.filter(e => e.parent_id == null && String(e.id) !== String(editId)));
      if (editing) {
        const e = third.equipment;
        setForm({
          make: e.make || '', model: e.model || '', year: e.year ?? '', serial: e.serial || '',
          category: e.category || 'other', hour_meter: '',
          purchase_date: e.purchase_date || '',
          purchase_price_cents: e.purchase_price_cents != null ? String(e.purchase_price_cents / 100) : '',
          primary_maintainer_id: e.primary_maintainer_id ?? '', backup_maintainer_id: e.backup_maintainer_id ?? '',
          parent_id: e.parent_id ?? '', notes: e.notes || '',
        });
      } else if (third.primary_maintainer_id) {
        setForm(f => ({ ...f, primary_maintainer_id: third.primary_maintainer_id }));
      }
    }).catch(e => setErr(e.message));
  }, [editId]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = {
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year ? Number(form.year) : null,
        serial: form.serial.trim() || null,
        category: form.category,
        hour_meter: form.hour_meter ? Number(form.hour_meter) : null,
        purchase_date: form.purchase_date || null,
        purchase_price_cents: form.purchase_price_cents ? Math.round(Number(form.purchase_price_cents) * 100) : null,
        primary_maintainer_id: form.primary_maintainer_id ? Number(form.primary_maintainer_id) : null,
        backup_maintainer_id: form.backup_maintainer_id ? Number(form.backup_maintainer_id) : null,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        notes: form.notes.trim() || null,
      };
      if (editing) {
        // The hour meter has its own card on the machine page; purchase fields are owner-only
        delete body.hour_meter;
        if (!isOwner) { delete body.purchase_date; delete body.purchase_price_cents; }
        await api.patch(`/api/equipment/${editId}`, body);
        navigate(`/equipment/${editId}`);
      } else {
        const { id } = await api.post('/api/equipment', body);
        navigate(`/equipment/${id}`);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5 max-w-xl">
      <h1 className="text-2xl font-serif text-olive-900">{editing ? 'Edit Equipment' : 'Add Equipment'}</h1>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Make *</label>
          <input className="input" required value={form.make} onChange={e => set('make', e.target.value)} placeholder="Kubota" />
        </div>
        <div>
          <label className="label">Model *</label>
          <input className="input" required value={form.model} onChange={e => set('model', e.target.value)} placeholder="M7060" />
        </div>
        <div>
          <label className="label">Year</label>
          <input className="input" type="number" value={form.year} onChange={e => set('year', e.target.value)} placeholder="2018" />
        </div>
        <div>
          <label className="label">Category</label>
          <select className="input" value={form.category} onChange={e => set('category', e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Serial #</label>
          <input className="input" value={form.serial} onChange={e => set('serial', e.target.value)} />
        </div>
        {!editing && <div>
          <label className="label">Hour meter (today's reading)</label>
          <input className="input" type="number" step="0.1" value={form.hour_meter} onChange={e => set('hour_meter', e.target.value)} placeholder="900" />
          <p className="text-xs text-ink-700/60 mt-1">The service schedule starts from here — the next service lands on the next interval mark (e.g. 1000).</p>
        </div>}
        {isOwner && (
          <div>
            <label className="label">Purchase date</label>
            <input className="input" type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
          </div>
        )}
        {isOwner && (
          <div className="col-span-2">
            <label className="label">Purchase price (USD, for CPA)</label>
            <input className="input" type="number" step="0.01" value={form.purchase_price_cents} onChange={e => set('purchase_price_cents', e.target.value)} placeholder="32500" />
          </div>
        )}
        <div>
          <label className="label">Primary maintainer</label>
          <select className="input" value={form.primary_maintainer_id} onChange={e => set('primary_maintainer_id', e.target.value)}>
            <option value="">—</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Backup maintainer</label>
          <select className="input" value={form.backup_maintainer_id} onChange={e => set('backup_maintainer_id', e.target.value)}>
            <option value="">—</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Parent equipment (for implements/attachments)</label>
          <select className="input" value={form.parent_id} onChange={e => set('parent_id', e.target.value)}>
            <option value="">— none (this is a standalone machine) —</option>
            {equipment.map(e => <option key={e.id} value={e.id}>{e.make} {e.model}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Notes</label>
          <textarea className="input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>

      {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

      <div className="flex gap-2">
        <button type="button" onClick={() => navigate(editing ? `/equipment/${editId}` : '/equipment')} className="btn-ghost">Cancel</button>
        <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </div>

      {!editing && <p className="text-xs text-ink-700/50">After save, the system attempts to fetch the operator + service manuals automatically. If the OEM gates them, you'll see an "Upload PDF" button on the detail page.</p>}
    </form>
  );
}
